import { PublicTeamDTO, TaskStatus, TeamTaskDTO, TeamTaskSubtaskDTO } from "shared/services";
import {
	SidebarTaskGroup,
	SidebarTaskSummary,
	TaskBoardState,
	TaskHeaderStats,
	TaskItemNode,
	TaskItemViewModel,
	TaskNode,
	TaskStatusCounts,
	TASK_STATUS_LABEL,
} from "./types";

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const toDate = (value: string | null | undefined): Date | null => {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return date;
};

const dateToKey = (date: Date): string => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

const maxDateIso = (values: Array<string | null | undefined>, fallback: string): string => {
	const times = values.map((value) => toDate(value)?.getTime() ?? null).filter((value): value is number => value !== null);

	if (times.length === 0) return fallback;

	return new Date(Math.max(...times)).toISOString();
};

const minDateIso = (values: Array<string | null | undefined>): string | null => {
	const times = values.map((value) => toDate(value)?.getTime() ?? null).filter((value): value is number => value !== null);

	if (times.length === 0) return null;

	return new Date(Math.min(...times)).toISOString();
};

const durationBetween = (start: string | null, end: string | null): number | null => {
	const startTime = toDate(start)?.getTime();
	const endTime = toDate(end)?.getTime();

	if (!startTime || !endTime) return null;
	if (endTime < startTime) return null;

	return endTime - startTime;
};

const pickRelevantSubtask = (subtasks: TeamTaskSubtaskDTO[]): TeamTaskSubtaskDTO | null => {
	if (subtasks.length === 0) return null;

	const processing = subtasks.find((subtask) => subtask.status === "processing");
	if (processing) return processing;

	const pending = subtasks.find((subtask) => subtask.status === "pending");
	if (pending) return pending;

	for (let index = subtasks.length - 1; index >= 0; index -= 1) {
		const current = subtasks[index];
		if (current.status === "completed" || current.status === "failed") {
			return current;
		}
	}

	return subtasks[subtasks.length - 1] ?? null;
};

const pickFailedSubtask = (subtasks: TeamTaskSubtaskDTO[]): TeamTaskSubtaskDTO | null => {
	const failed = subtasks.filter((subtask) => subtask.status === "failed");
	if (failed.length === 0) return null;

	return failed.reduce((latest, current) => {
		const latestTime = toDate(latest.updatedAt)?.getTime() ?? 0;
		const currentTime = toDate(current.updatedAt)?.getTime() ?? 0;
		return currentTime > latestTime ? current : latest;
	});
};

export const createEmptyTaskBoardState = (): TaskBoardState => ({
	taskIds: [],
	taskById: {},
	taskItemById: {},
	subtaskById: {},
	toolCallBySubtaskId: {},
	memberNamesById: {},
	selectedTaskId: null,
	loadingTasks: false,
	loadingMembers: false,
});

export const computeTaskItemStatus = (subtasks: TeamTaskSubtaskDTO[]): TaskStatus => {
	if (subtasks.length === 0) return "pending";

	const total = subtasks.length;
	const completedCount = subtasks.filter((subtask) => subtask.status === "completed").length;

	if (subtasks.some((subtask) => subtask.status === "failed")) return "failed";
	if (completedCount === total) return "completed";
	if (subtasks.some((subtask) => subtask.status === "processing")) return "processing";
	if (subtasks.some((subtask) => subtask.status === "pending")) return "pending";
	if (subtasks.some((subtask) => subtask.status === "paused")) return "paused";

	return "pending";
};

export const buildTaskSearchBlob = (task: TeamTaskDTO): string => {
	const values: string[] = [task.objective, task.systemPrompt];

	for (const item of task.items) {
		values.push(item.title, item.description ?? "", item.objective);
		for (const subtask of item.subtasks) {
			values.push(subtask.title, subtask.description ?? "", subtask.objective, subtask.statusDescription);
		}
	}

	return values.join(" ").toLowerCase();
};

export const normalizeWorkspaceTasks = (
	tasks: TeamTaskDTO[],
): Pick<TaskBoardState, "taskById" | "taskItemById" | "subtaskById" | "taskIds"> => {
	const taskById: TaskBoardState["taskById"] = {};
	const taskItemById: TaskBoardState["taskItemById"] = {};
	const subtaskById: TaskBoardState["subtaskById"] = {};

	for (const task of tasks) {
		const itemIds: string[] = [];

		for (const item of task.items) {
			itemIds.push(item.id);

			const subtaskIds: string[] = [];
			for (const subtask of item.subtasks) {
				subtaskIds.push(subtask.id);
				subtaskById[subtask.id] = subtask;
			}

			taskItemById[item.id] = {
				id: item.id,
				taskId: item.taskId,
				title: item.title,
				description: item.description,
				objective: item.objective,
				createdAt: item.createdAt,
				updatedAt: item.updatedAt,
				subtaskIds,
			};
		}

		taskById[task.id] = {
			id: task.id,
			teamId: task.teamId,
			workspaceId: task.workspaceId,
			accountId: task.accountId,
			objective: task.objective,
			systemPrompt: task.systemPrompt,
			status: task.status ?? "active",
			cancelledAt: task.cancelledAt ?? null,
			cancelledBy: task.cancelledBy ?? null,
			cancellationReason: task.cancellationReason ?? null,
			createdAt: task.createdAt,
			updatedAt: task.updatedAt,
			itemIds,
		};
	}

	const taskIds = Object.values(taskById)
		.sort((left, right) => {
			const leftTime = toDate(left.updatedAt)?.getTime() ?? 0;
			const rightTime = toDate(right.updatedAt)?.getTime() ?? 0;
			return rightTime - leftTime;
		})
		.map((task) => task.id);

	return {
		taskById,
		taskItemById,
		subtaskById,
		taskIds,
	};
};

export const applySubtaskRealtimeUpdate = (
	state: TaskBoardState,
	event: {
		taskId: string;
		taskItemId: string;
		subtaskId: string;
		status: TaskStatus;
		statusDescription: string;
		capacityUsed: number;
		usedMcps: string[];
		updatedAt: string;
		completedAt: string | null;
		reviewStatus?: "approved" | "rejected" | "needs_changes" | null;
	},
): TaskBoardState | null => {
	const currentSubtask = state.subtaskById[event.subtaskId];
	const currentItem = state.taskItemById[event.taskItemId];
	const currentTask = state.taskById[event.taskId];

	if (!currentSubtask || !currentItem || !currentTask) {
		return null;
	}

	const nextSubtask = {
		...currentSubtask,
		status: event.status,
		statusDescription: event.statusDescription,
		capacityUsed: event.capacityUsed,
		usedMcps: event.usedMcps,
		updatedAt: event.updatedAt,
		completedAt: event.completedAt,
		// Atualiza review_status quando o evento traz (review por subtarefa).
		...(event.reviewStatus !== undefined ? { reviewStatus: event.reviewStatus } : {}),
	};

	return {
		...state,
		subtaskById: {
			...state.subtaskById,
			[event.subtaskId]: nextSubtask,
		},
		taskItemById: {
			...state.taskItemById,
			[event.taskItemId]: {
				...currentItem,
				updatedAt: maxDateIso([currentItem.updatedAt, event.updatedAt], currentItem.updatedAt),
			},
		},
		taskById: {
			...state.taskById,
			[event.taskId]: {
				...currentTask,
				updatedAt: maxDateIso([currentTask.updatedAt, event.updatedAt], currentTask.updatedAt),
			},
		},
	};
};

export const buildSidebarTaskSummaries = (state: TaskBoardState, teams: PublicTeamDTO[], search: string): SidebarTaskSummary[] => {
	const teamNameById = teams.reduce(
		(acc, team) => {
			acc[String(team.id)] = team.name;
			return acc;
		},
		{} as Record<string, string>,
	);

	const normalizedSearch = search.trim().toLowerCase();

	const summaries = state.taskIds
		.map((taskId) => state.taskById[taskId])
		.filter((task): task is TaskNode => Boolean(task))
		.map((task) => {
			const items = task.itemIds.map((itemId) => state.taskItemById[itemId]).filter((item): item is TaskItemNode => Boolean(item));

			const completedItems = items.reduce((count, item) => {
				const subtasks = item.subtaskIds
					.map((subtaskId) => state.subtaskById[subtaskId])
					.filter((subtask): subtask is TeamTaskSubtaskDTO => Boolean(subtask));

				return computeTaskItemStatus(subtasks) === "completed" ? count + 1 : count;
			}, 0);

			const searchBlob = [
				task.objective,
				task.systemPrompt,
				...items.map((item) => `${item.title} ${item.description ?? ""} ${item.objective}`),
			]
				.join(" ")
				.toLowerCase();

			return {
				id: task.id,
				teamId: task.teamId,
				teamName: teamNameById[task.teamId] ?? "Equipe",
				objective: task.objective,
				updatedAt: task.updatedAt,
				totalItems: items.length,
				completedItems,
				searchBlob,
			};
		});

	if (!normalizedSearch) {
		return summaries;
	}

	return summaries.filter((task) => task.searchBlob.includes(normalizedSearch));
};

export const groupSidebarTasksByDay = (tasks: SidebarTaskSummary[]): SidebarTaskGroup[] => {
	const now = new Date();
	const todayKey = dateToKey(now);
	const yesterdayKey = dateToKey(new Date(now.getTime() - DAY_IN_MS));

	const groups = new Map<string, SidebarTaskGroup>();

	for (const task of tasks) {
		const taskDate = toDate(task.updatedAt) ?? new Date();
		const dayKey = dateToKey(taskDate);

		let label = taskDate.toLocaleDateString("pt-BR");
		if (dayKey === todayKey) label = "Hoje";
		if (dayKey === yesterdayKey) label = "Ontem";

		if (!groups.has(dayKey)) {
			groups.set(dayKey, {
				key: dayKey,
				label,
				tasks: [],
			});
		}

		groups.get(dayKey)?.tasks.push(task);
	}

	return Array.from(groups.values()).sort((left, right) => {
		const leftDate = toDate(left.tasks[0]?.updatedAt)?.getTime() ?? 0;
		const rightDate = toDate(right.tasks[0]?.updatedAt)?.getTime() ?? 0;
		return rightDate - leftDate;
	});
};

export const buildTaskItemViewModels = (state: TaskBoardState, taskId: string | null, teams: PublicTeamDTO[]): TaskItemViewModel[] => {
	if (!taskId) return [];

	const task = state.taskById[taskId];
	if (!task) return [];

	const teamName = teams.find((team) => String(team.id) === task.teamId)?.name ?? "Equipe";
	const memberNames = state.memberNamesById;

	return task.itemIds
		.map((itemId) => state.taskItemById[itemId])
		.filter((item): item is TaskItemNode => Boolean(item))
		.map((item) => {
			const subtasks = item.subtaskIds
				.map((subtaskId) => state.subtaskById[subtaskId])
				.filter((subtask): subtask is TeamTaskSubtaskDTO => Boolean(subtask));

			const totalSubtasks = subtasks.length;
			const completedSubtasks = subtasks.filter((subtask) => subtask.status === "completed").length;
			const progress = totalSubtasks > 0 ? Math.round((completedSubtasks / totalSubtasks) * 100) : 0;

			const status = computeTaskItemStatus(subtasks);
			const relevantSubtask = pickRelevantSubtask(subtasks);
			const failedSubtask = pickFailedSubtask(subtasks);

			const startedAt = minDateIso(subtasks.map((subtask) => subtask.startedAt));
			const completedAt =
				status === "completed"
					? maxDateIso(
							subtasks.map((subtask) => subtask.completedAt),
							item.updatedAt,
						)
					: null;

			const alerts = [] as TaskItemViewModel["alerts"];
			if (failedSubtask?.statusDescription) {
				alerts.push({
					type: "error",
					label: "Falha",
					value: failedSubtask.statusDescription,
				});
			}

			if (relevantSubtask?.usedMcps?.length) {
				alerts.push({
					type: "info",
					label: "MCPs",
					value: `${relevantSubtask.usedMcps.length} ativo(s)`,
				});
			}

			return {
				id: item.id,
				title: item.title,
				description: relevantSubtask?.title ?? item.description ?? item.objective,
				status,
				statusLabel: TASK_STATUS_LABEL[status],
				progress,
				errorDescription: failedSubtask?.statusDescription ?? null,
				alerts,
				activeMemberId: relevantSubtask?.teamMemberId ?? null,
				activeMemberName: relevantSubtask?.teamMemberId ? (memberNames[relevantSubtask.teamMemberId] ?? "Agente") : "Sem agente",
				teamName,
				startedAt,
				completedAt,
				durationMs: durationBetween(startedAt, completedAt),
				updatedAt: maxDateIso([item.updatedAt, ...subtasks.map((subtask) => subtask.updatedAt)], item.updatedAt),
			};
		});
};

export const filterTaskItems = (items: TaskItemViewModel[], statusFilter: "all" | TaskStatus): TaskItemViewModel[] => {
	return items.filter((item) => {
		if (statusFilter !== "all" && item.status !== statusFilter) {
			return false;
		}
		return true;
	});
};

export const buildTaskStatusCounts = (items: TaskItemViewModel[]): TaskStatusCounts => {
	const counts: TaskStatusCounts = {
		all: items.length,
		pending: 0,
		processing: 0,
		completed: 0,
		failed: 0,
		paused: 0,
		cancelled: 0,
	};

	for (const item of items) {
		counts[item.status] += 1;
	}

	return counts;
};

export const buildTaskHeaderStats = (items: TaskItemViewModel[]): TaskHeaderStats => {
	const now = new Date();
	const todayKey = dateToKey(now);

	const completedToday = items.filter((item) => {
		if (item.status !== "completed") return false;
		const completedDate = toDate(item.completedAt);
		if (!completedDate) return false;
		return dateToKey(completedDate) === todayKey;
	}).length;

	const processing = items.filter((item) => item.status === "processing").length;
	const waiting = items.filter((item) => item.status === "pending" || item.status === "paused").length;
	const failed = items.filter((item) => item.status === "failed").length;

	return {
		completedToday,
		processing,
		waiting,
		failed,
	};
};

export const formatClock = (value: string | null): string => {
	const date = toDate(value);
	if (!date) return "--:--";
	return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

export const formatDuration = (durationMs: number | null): string => {
	if (durationMs === null || durationMs < 0) return "--";

	if (durationMs < 1000) {
		return `${durationMs} MS`;
	}

	const totalSeconds = Math.floor(durationMs / 1000);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	if (hours > 0) {
		return `${hours}H ${minutes.toString().padStart(2, "0")}M`;
	}

	if (minutes > 0) {
		return `${minutes}M ${seconds.toString().padStart(2, "0")}S`;
	}

	return `${seconds}S`;
};
